import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { TOTAL_LOSS_ASSIST } from '@app/constants/feature-definitions';
import { CASE_AI_ASSISTANTS } from '@app/constants/permissions';
import { agentEventProcessor } from '@app/helpers/agent-event-processor';
import { eventProcessor } from '@app/helpers/event-processor';
import { jobEventProcessor } from '@app/helpers/job-event-processor';
import usePermissionVerify from '@app/hooks/permission-verify';
import { useAppSelector } from '@app/hooks/redux';
import { useCaseVisibilityScope } from '@app/hooks/use-case-visibility-scope';
import { useUnaddressed } from '@app/hooks/use-unaddressed';
import { useIsFeatureEnabled } from '@app/services/organization-features';

interface TopicMessageEvent {
  detail?: {
    payload?: Array<{
      name: string;
      payload: unknown;
      userId: string;
    }>;
  };
}

const useTopicHandler = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const userId = useAppSelector(
    (state) => state?.auth?.user?._id || state?.auth?.user?.id || '',
  );
  const jwtPermissions = useAppSelector(
    (state) => state?.auth?.permissions || [],
  );

  const hasTotalLossEnabled = useIsFeatureEnabled(TOTAL_LOSS_ASSIST, {
    failClosed: true,
  });
  const permissions = useMemo(() => {
    const withoutTotalLoss = jwtPermissions.filter(
      (permission: string) => permission !== TOTAL_LOSS_ASSIST,
    );
    return hasTotalLossEnabled
      ? [...withoutTotalLoss, TOTAL_LOSS_ASSIST]
      : withoutTotalLoss;
  }, [jwtPermissions, hasTotalLossEnabled]);
  const permissionsMap = usePermissionVerify([CASE_AI_ASSISTANTS]);
  const isFabricAgentsEnabled = !!permissionsMap[CASE_AI_ASSISTANTS];
  const { unaddressedEnabled, suppressionEnabled } = useUnaddressed(userId);
  const caseVisibility = useCaseVisibilityScope();
  const memoizedTopicHandler = useMemo(
    () =>
      (
        messageEvent: CustomEvent<{
          payload?: Array<{ name: string; payload: unknown; userId: string }>;
        }>,
      ) => {
        const event: TopicMessageEvent = messageEvent;
        if (isFabricAgentsEnabled) {
          agentEventProcessor(queryClient)(event);
        }
        jobEventProcessor(
          queryClient,
          userId,
          permissions,
          unaddressedEnabled,
          suppressionEnabled,
          caseVisibility,
        )(event);
        eventProcessor(dispatch, queryClient, caseVisibility)(event);
      },
    [
      dispatch,
      queryClient,
      userId,
      permissions,
      isFabricAgentsEnabled,
      unaddressedEnabled,
      suppressionEnabled,
      caseVisibility,
    ],
  );
  return memoizedTopicHandler;
};
export default useTopicHandler;
